import { ArrowLeft, Bot, CalendarCheck, Check, ChevronRight, Download, Eye, LoaderCircle, Maximize2, Menu, MessageCircle, MessageSquareText, Plus, Send, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { Link } from "react-router-dom";
import { api, apiBlob, apiEventStream } from "../api";
import { useMediaQuery } from "../hooks";

type Candidate = { id: string; label: string; detail?: string };
type AgentItem = {
  serviceQuery: string; serviceTypeId?: string; serviceName?: string; quantity: string;
  unitPrice?: string; discount: string; candidates?: Candidate[];
};
type Preview = {
  items: Array<{ serviceName: string; quantity: string; unit: string; unitPrice: string; discount: string; total: string }>;
  subtotal: string; discount: string; surcharge: string; total: string; validUntil: string;
  customerName: string; locationLabel: string; paymentMethod: string;
};
type SchedulePreview = {
  sourceLabel: string; customerName: string; locationLabel: string; serviceName: string;
  startsAt: string; endsAt: string; responsibleName: string; agreedValue: string;
  visits?: Array<{ startsAt: string; endsAt: string }>;
};
type CustomerPreview = {
  kind: "individual" | "company"; legalName: string; tradeName?: string; taxId?: string;
  phone?: string; whatsapp?: string; email?: string; customerType?: string; notes?: string;
  location?: { postalCode?: string; street?: string; number?: string; complement?: string; district?: string; city?: string; state?: string; reference?: string };
};
type LocationPreview = {
  customerId: string; customerName: string; postalCode?: string; street: string; number?: string;
  complement?: string; district: string; city: string; state: string; reference?: string;
};
type StructuredMessage = {
  actionLink?: { to: string; label: string };
  quickReplies?: string[];
  financeSummary?: { period: "week" | "month" | "year"; received: string; costs: string; profit: string; receivable: string; overdue: string };
  schedulePreview?: SchedulePreview;
  scheduleResult?: { appointmentId: string; workOrderId: string; workOrderNumber: string };
  customerPreview?: CustomerPreview;
  customerResult?: { customerId: string; legalName: string; duplicateWarnings: number };
  locationPreview?: LocationPreview;
  locationResult?: { locationId: string; customerId: string; customerName: string; address: string; duplicateWarnings: number };
  documentResult?: { kind: string; label: string; fileUrl: string; number?: string };
  fiscalPreview?: { workOrderNumber: string; customerName: string; total: string; groups: Array<{ key: string; amount: string; description: string }> };
  fiscalIssues?: Array<{ code: string; message: string; action: string }>;
  fiscalStatus?: { status: string; nfseNumber?: string | null; amount: string };
  fiscalResult?: { workOrderNumber: string; documents: Array<{ id: string; status: string; amount: string; description: string }> };
};
type ConversationMessage = {
  id: string; role: "user" | "assistant" | "system"; content: string; createdAt: string; structured?: StructuredMessage;
};
type Conversation = {
  id: string; title: string; status: string; resultQuoteId: string | null; canDelete: boolean;
  ownerUserId: string; ownerName: string | null; ownerEmail: string | null; readOnly: boolean;
  channel: "app" | "whatsapp"; channelContactName: string | null; channelPhoneLast4: string | null;
  state: {
    customerCandidates?: Candidate[]; locationCandidates?: Candidate[]; items: AgentItem[];
    missing: string[]; preview?: Preview;
    schedule?: { missing: string[]; responsibleCandidates?: Candidate[]; preview?: SchedulePreview };
    customer?: { customerId?: string; missing: string[]; preview?: CustomerPreview; locationPreview?: LocationPreview };
  };
  messages: ConversationMessage[];
  pendingAction: { id: string; type: "create_quote" | "create_appointment" | "create_customer" | "create_location" | "issue_fiscal" | "transition_work_order" | "report_field_cost" | "report_material" | "report_payment" | "report_occurrence" | "reschedule_appointment" | "cancel_appointment"; expiresAt: string } | null;
};
type ConversationSummary = {
  id: string; title: string; status: string; updatedAt: string; resultQuoteId: string | null; canDelete: boolean;
  ownerUserId: string; ownerName: string | null; ownerEmail: string | null; readOnly: boolean;
  channel: "app" | "whatsapp"; channelContactName: string | null; channelPhoneLast4: string | null;
};
type Selection = { kind: "customer" | "location" | "service" | "employee"; id: string; itemIndex?: number };

const terminalStatuses = new Set(["completed", "cancelled", "expired"]);
const welcomeMessage = "Oi! Me conta o que você precisa resolver agora.";
const thinkingDelayMs = 220;
const assistantRevealDelayMs = 560;
const conversationOwner = (conversation: Pick<ConversationSummary, "ownerName" | "ownerEmail">) => (
  conversation.ownerName?.trim() || conversation.ownerEmail || "Usuário"
);
const brl = (value: string) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
const paymentLabels: Record<string, string> = {
  pix: "Pix", cash: "Dinheiro", credit_card: "Cartão de crédito", debit_card: "Cartão de débito",
  boleto: "Boleto", bank_transfer: "Transferência", other: "Outro",
};

export function AgentPage({ presentation = "page", onClose }: {
  presentation?: "page" | "panel";
  onClose?: () => void;
}) {
  const isMobile = useMediaQuery("(max-width: 780px)");
  const freshDraftMode = presentation === "panel" || isMobile;
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [message, setMessage] = useState("");
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(null);
  const [liveAssistantText, setLiveAssistantText] = useState("");
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null);
  const [busyLabel, setBusyLabel] = useState("Pensando…");
  const [showThinking, setShowThinking] = useState(false);
  const [canRevealAssistant, setCanRevealAssistant] = useState(false);
  const [error, setError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(420);
  const sidebarResizeRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const previousConversationIdRef = useRef<string | null>(null);
  const feedbackTimersRef = useRef<number[]>([]);

  const scrollToLatest = (behavior: ScrollBehavior | "slow" = "smooth") => {
    const chat = chatRef.current;
    if (!chat) return;
    if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    scrollAnimationRef.current = null;
    if (behavior !== "slow" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      chat.scrollTo({ top: chat.scrollHeight, behavior: behavior === "slow" ? "auto" : behavior });
      return;
    }
    const start = chat.scrollTop;
    const target = Math.max(0, chat.scrollHeight - chat.clientHeight);
    const distance = target - start;
    if (Math.abs(distance) < 2) return;
    const startedAt = performance.now();
    const duration = 900;
    const animate = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      chat.scrollTop = start + distance * eased;
      if (progress < 1) scrollAnimationRef.current = requestAnimationFrame(animate);
      else scrollAnimationRef.current = null;
    };
    scrollAnimationRef.current = requestAnimationFrame(animate);
  };

  const loadList = async () => {
    const result = await api<{ data: ConversationSummary[] }>("/agent/conversations?pageSize=20");
    setConversations(result.data);
    return result.data;
  };

  const revealNewAssistantMessage = (updated: Conversation, knownMessageIds: Set<string>) => {
    const latest = updated.messages.findLast(({ id, role }) => role === "assistant" && !knownMessageIds.has(id));
    setConversation(updated);
    setStreamingMessageId(latest?.id ?? null);
  };

  const open = async (conversationId: string) => {
    setBusy(true);
    setBusyLabel("Abrindo conversa…");
    setError("");
    setStreamingMessageId(null);
    try {
      setConversation(await api<Conversation>(`/agent/conversations/${conversationId}`));
      setHistoryOpen(false);
    }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível abrir a conversa."); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    if (freshDraftMode) setConversation(null);
    void (async () => {
      try {
        const list = await loadList();
        if (!freshDraftMode) {
          const current = list.find(({ status }) => !terminalStatuses.has(status)) ?? list[0];
          if (current) {
            const detail = await api<Conversation>(`/agent/conversations/${current.id}`);
            if (!cancelled) setConversation(detail);
          }
        }
      } catch (caught) { setError(caught instanceof Error ? caught.message : "Assistente indisponível."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [freshDraftMode]);

  useEffect(() => {
    if (!freshDraftMode || conversation || busy || loading) return;
    let focusFrame: number | null = null;
    const focusComposer = () => {
      focusFrame = window.requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
    };
    focusComposer();
    return () => {
      if (focusFrame !== null) window.cancelAnimationFrame(focusFrame);
    };
  }, [busy, conversation, freshDraftMode, loading]);

  useEffect(() => {
    const conversationId = conversation?.id ?? null;
    const changedConversation = conversationId !== null && conversationId !== previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    scrollToLatest(changedConversation ? "slow" : "smooth");
  }, [conversation?.id, conversation?.messages.length, optimisticMessage, liveAssistantText, canRevealAssistant]);

  useEffect(() => () => {
    if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    feedbackTimersRef.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const clearFeedbackTimers = () => {
    feedbackTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    feedbackTimersRef.current = [];
  };

  const beginAssistantFeedback = () => {
    clearFeedbackTimers();
    setShowThinking(false);
    setCanRevealAssistant(false);
    const startedAt = performance.now();
    feedbackTimersRef.current = [
      window.setTimeout(() => setShowThinking(true), thinkingDelayMs),
      window.setTimeout(() => setCanRevealAssistant(true), assistantRevealDelayMs),
    ];
    return startedAt + assistantRevealDelayMs;
  };

  useEffect(() => {
    if (!historyOpen) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setHistoryOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [historyOpen]);

  const create = async (animateGreeting = true) => {
    setBusy(true);
    setBusyLabel("Começando uma conversa…");
    setError("");
    setOptimisticMessage(null);
    try {
      const created = await api<Conversation>("/agent/conversations", { method: "POST", body: JSON.stringify({}) });
      setConversation(created);
      setStreamingMessageId(animateGreeting ? created.messages.findLast(({ role }) => role === "assistant")?.id ?? null : null);
      setMessage("");
      setHistoryOpen(false);
      await loadList();
      window.setTimeout(() => composerRef.current?.focus(), 0);
      return created;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível iniciar.");
      return null;
    } finally { setBusy(false); }
  };

  const startNewConversation = () => {
    if (!freshDraftMode) {
      void create();
      return;
    }
    setConversation(null);
    setMessage("");
    setOptimisticMessage(null);
    setLiveAssistantText("");
    setStreamingMessageId(null);
    setError("");
    setHistoryOpen(false);
    window.requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
  };

  const removeEmptyConversation = async (item: Pick<Conversation, "id" | "title" | "canDelete">) => {
    if (!item.canDelete || busy || deletingConversationId) return;
    setDeletingConversationId(item.id);
    setError("");
    try {
      await api(`/agent/conversations/${item.id}`, { method: "DELETE" });
      const remaining = await loadList();
      if (conversation?.id === item.id) {
        setStreamingMessageId(null);
        if (freshDraftMode) {
          setConversation(null);
          window.requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
        } else {
          const next = remaining.find(({ status }) => !terminalStatuses.has(status)) ?? remaining[0];
          setConversation(next ? await api<Conversation>(`/agent/conversations/${next.id}`) : null);
        }
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível excluir a conversa.");
    } finally {
      setDeletingConversationId(null);
    }
  };

  const send = async (content: string, selections: Selection[] = []) => {
    const cleanContent = content.trim();
    if (!cleanContent || busy || conversation?.readOnly) return;
    let target = conversation;
    const revealAt = beginAssistantFeedback();
    setBusy(true);
    setBusyLabel("Pensando…");
    setError("");
    setMessage("");
    setOptimisticMessage(cleanContent);
    setLiveAssistantText("");
    setStreamingMessageId(null);
    try {
      if (!target || terminalStatuses.has(target.status)) {
        target = await api<Conversation>("/agent/conversations", { method: "POST", body: JSON.stringify({}) });
        setConversation(target);
      }
      const path = `/agent/conversations/${target.id}/messages/stream`;
      let updated: Conversation | undefined;
      await apiEventStream(path, {
        method: "POST", body: JSON.stringify({ content: cleanContent, selections }),
      }, ({ event, data }) => {
        if (event === "status" && data && typeof data === "object" && "label" in data && typeof data.label === "string") {
          setBusyLabel(data.label);
        } else if (event === "delta" && data && typeof data === "object" && "text" in data && typeof data.text === "string") {
          setBusyLabel("Respondendo…");
          setLiveAssistantText((current) => current + data.text);
        } else if (event === "done") {
          updated = data as Conversation;
        } else if (event === "error") {
          const streamMessage = data && typeof data === "object" && "message" in data && typeof data.message === "string"
            ? data.message
            : "Não foi possível processar a mensagem.";
          throw new Error(streamMessage);
        }
      });
      if (!updated) throw new Error("A resposta em tempo real terminou antes da confirmação.");
      const revealWait = revealAt - performance.now();
      if (revealWait > 0) await new Promise((resolve) => window.setTimeout(resolve, revealWait));
      setOptimisticMessage(null);
      setLiveAssistantText("");
      setConversation(updated);
      await loadList();
    } catch (caught) {
      clearFeedbackTimers();
      setShowThinking(false);
      setCanRevealAssistant(false);
      setOptimisticMessage(null);
      setLiveAssistantText("");
      setMessage(selections.length ? "" : cleanContent);
      setError(caught instanceof Error ? caught.message : "Não foi possível processar a mensagem.");
      if (target) {
        try { setConversation(await api<Conversation>(`/agent/conversations/${target.id}`)); } catch { /* mantém a conversa já visível */ }
      }
    } finally {
      clearFeedbackTimers();
      setShowThinking(false);
      setCanRevealAssistant(false);
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => { event.preventDefault(); void send(message); };
  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (message.trim()) void send(message);
    }
  };

  const resizeSidebar = (width: number) => setSidebarWidth(Math.max(224, Math.min(420, width)));
  const startSidebarResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    sidebarResizeRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: sidebarWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveSidebarResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    const resize = sidebarResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    resizeSidebar(resize.startWidth + event.clientX - resize.startX);
  };
  const stopSidebarResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (sidebarResizeRef.current?.pointerId !== event.pointerId) return;
    sidebarResizeRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const confirm = async () => {
    if (!conversation?.pendingAction || conversation.readOnly || busy) return;
    const knownMessageIds = new Set(conversation.messages.map(({ id }) => id));
    setBusy(true);
    setBusyLabel("Concluindo…");
    setError("");
    try {
      await api(`/agent/conversations/${conversation.id}/actions/${conversation.pendingAction.id}/confirm`, {
        method: "POST", body: JSON.stringify({}),
      });
      const updated = await api<Conversation>(`/agent/conversations/${conversation.id}`);
      revealNewAssistantMessage(updated, knownMessageIds);
      await loadList();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível concluir a ação."); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!conversation?.pendingAction || conversation.readOnly || busy) return;
    const knownMessageIds = new Set(conversation.messages.map(({ id }) => id));
    setBusy(true);
    setBusyLabel("Descartando…");
    setError("");
    try {
      await api(`/agent/conversations/${conversation.id}/actions/${conversation.pendingAction.id}/cancel`, {
        method: "POST", body: JSON.stringify({}),
      });
      const updated = await api<Conversation>(`/agent/conversations/${conversation.id}`);
      revealNewAssistantMessage(updated, knownMessageIds);
      await loadList();
      window.setTimeout(() => composerRef.current?.focus(), 0);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível descartar."); }
    finally { setBusy(false); }
  };

  const hasConversation = Boolean(conversation);
  const canShowContext = Boolean(conversation && !optimisticMessage && !busy);
  const hasOrganizationHistory = conversations.some(({ readOnly }) => readOnly);
  const latestAssistantId = conversation?.messages.findLast(({ role }) => role === "assistant")?.id;
  const visibleMessages = conversation?.messages.filter(({ role, content }, index) => (
    role !== "system" && !(freshDraftMode && index === 0 && role === "assistant" && content === welcomeMessage)
  ));
  const composerPlaceholder = conversation?.readOnly
    ? "Somente leitura"
    : terminalStatuses.has(conversation?.status ?? "")
      ? "O que mais você precisa?"
      : "Escreva uma mensagem…";

  return <div className={`agent-page agent-page-${presentation}${historyOpen ? " is-history-open" : ""}`}>
    <button className="agent-history-backdrop" type="button" aria-label="Fechar histórico" tabIndex={historyOpen ? 0 : -1} onClick={() => setHistoryOpen(false)} />
    <div className="agent-layout" style={presentation === "page" ? ({ "--agent-sidebar-width": `${sidebarWidth}px` } as CSSProperties) : undefined}>
      <aside className={`agent-sidebar${conversations.length === 0 ? " is-empty" : ""}`} aria-label="Histórico de conversas" aria-hidden={presentation === "panel" && !historyOpen ? true : undefined}>
        <header className="agent-sidebar-header">
          {presentation === "page" && <Link className="agent-icon-button agent-sidebar-back" to="/" aria-label="Voltar para a plataforma"><ArrowLeft size={20} /></Link>}
          <span className="agent-sidebar-title"><strong>{hasOrganizationHistory ? "Conversas da equipe" : "Conversas"}</strong>{hasOrganizationHistory && <small>As suas e as dos demais usuários</small>}</span>
          <button type="button" className="agent-icon-button agent-sidebar-close" aria-label="Fechar histórico" onClick={() => setHistoryOpen(false)}><X size={19} /></button>
        </header>
        <button className="agent-new-conversation" type="button" onClick={startNewConversation} disabled={busy}><Plus size={17} />Nova conversa</button>
        <div className="agent-conversation-list">
          {conversations.map((item) => <button key={item.id} className={conversation?.id === item.id ? "active" : ""} onClick={() => void open(item.id)}>
            {item.channel === "whatsapp"
              ? <MessageCircle className="agent-channel-icon" size={16} role="img" aria-label="Conversa WhatsApp" />
              : <MessageSquareText size={16} />}
            <span>
              <strong>{item.channel === "whatsapp" ? (item.channelContactName?.trim() || "WhatsApp") : item.title}</strong>
              <small>{item.channel === "whatsapp"
                ? `•••• ${item.channelPhoneLast4 ?? ""} · ${new Date(item.updatedAt).toLocaleDateString("pt-BR")}`
                : `${item.readOnly ? `${conversationOwner(item)} · ` : ""}${new Date(item.updatedAt).toLocaleDateString("pt-BR")}`}</small>
            </span>
          </button>)}
          {!loading && conversations.length === 0 && <p className="agent-history-empty">Sua primeira conversa aparecerá aqui.</p>}
        </div>
      </aside>
      {presentation === "page" && <div
        className="agent-sidebar-resizer"
        role="separator"
        aria-label="Redimensionar barra de conversas"
        aria-orientation="vertical"
        aria-valuemin={224}
        aria-valuemax={420}
        aria-valuenow={sidebarWidth}
        tabIndex={0}
        onPointerDown={startSidebarResize}
        onPointerMove={moveSidebarResize}
        onPointerUp={stopSidebarResize}
        onPointerCancel={stopSidebarResize}
        onDoubleClick={() => setSidebarWidth(420)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") { event.preventDefault(); resizeSidebar(sidebarWidth - 12); }
          if (event.key === "ArrowRight") { event.preventDefault(); resizeSidebar(sidebarWidth + 12); }
          if (event.key === "Home") { event.preventDefault(); setSidebarWidth(224); }
          if (event.key === "End") { event.preventDefault(); setSidebarWidth(420); }
        }}
      />}
      <section className="agent-workspace" aria-busy={busy}>
          <header className="agent-chat-header">
            <div className="agent-chat-heading">
              {presentation === "page" && <Link className="agent-icon-button agent-chat-back" to="/" aria-label="Voltar para a plataforma"><ArrowLeft size={20} /></Link>}
              <button type="button" className="agent-icon-button agent-history-trigger" aria-label="Abrir histórico de conversas" aria-expanded={historyOpen} onClick={() => setHistoryOpen(true)}><Menu size={20} /></button>
              <span className="agent-brand-avatar" aria-hidden="true"><Bot size={20} strokeWidth={2} /></span>
              {conversation?.channel === "whatsapp" ? <span>
                <strong className="agent-channel-title">{conversation.channelContactName?.trim() || "WhatsApp"}</strong>
                <small>WhatsApp · •••• {conversation.channelPhoneLast4 ?? ""}</small>
              </span> : <span>
                <strong>Assistente JA</strong><small>{conversation?.title ?? (freshDraftMode ? "Nova conversa" : "Pronto para ajudar")}</small>
              </span>}
            </div>
            <div className="agent-chat-actions">
              {presentation === "panel" && <Link className="agent-icon-button" to="/assistente" aria-label="Abrir tela completa" title="Abrir tela completa"><Maximize2 size={19} /></Link>}
              <button className="agent-icon-button agent-header-new" type="button" onClick={startNewConversation} disabled={busy} aria-label="Nova conversa" title="Nova conversa"><Plus size={20} /></button>
              <button
                className="agent-icon-button agent-header-delete"
                type="button"
                disabled={!conversation?.canDelete || busy || deletingConversationId !== null}
                aria-label={conversation?.canDelete ? `Excluir conversa vazia: ${conversation.title}` : "A conversa atual possui interações e não pode ser excluída"}
                title={conversation?.canDelete ? "Excluir conversa vazia" : "Só conversas sem interação podem ser excluídas"}
                onClick={() => conversation && void removeEmptyConversation(conversation)}
              >
                {deletingConversationId === conversation?.id ? <LoaderCircle className="spin" size={18} /> : <Trash2 size={18} />}
              </button>
              {presentation === "panel" && <button className="agent-icon-button" type="button" onClick={onClose} aria-label="Fechar assistente" title="Fechar"><X size={20} /></button>}
            </div>
          </header>
          {conversation?.readOnly && <div className="agent-admin-readonly" role="status">
            <Eye size={16} aria-hidden="true" />
            <span><strong>Visualização administrativa</strong>Conversa de {conversationOwner(conversation)}. O conteúdo está disponível somente para leitura.</span>
          </div>}
          <div ref={chatRef} className="agent-chat" aria-live="polite" aria-label="Conversa com o Assistente JA">
            {loading && !freshDraftMode && <div className="agent-loading" role="status"><LoaderCircle className="spin" size={24} /><span>Preparando sua conversa…</span></div>}
            {!hasConversation && freshDraftMode && !optimisticMessage && <div className="agent-new-chat-canvas">
              <span className="agent-brand-avatar" aria-hidden="true"><Bot size={22} strokeWidth={2} /></span>
              <strong>Como posso ajudar?</strong>
              <small>Escreva o que você precisa resolver na JA.</small>
            </div>}
            {!loading && !hasConversation && !freshDraftMode && <AgentMessageBubble content={welcomeMessage} />}
            {visibleMessages?.map((item) => <article key={item.id} className={`agent-message agent-message-${item.role}`}>
              {item.role === "assistant" && <span className="agent-avatar" aria-hidden="true"><Bot size={18} strokeWidth={2} /></span>}
              <div className="agent-message-body">
                <p>{item.id === streamingMessageId
                  ? <ProgressiveText text={item.content} onProgress={() => scrollToLatest("auto")} onDone={() => setStreamingMessageId(null)} />
                  : <FormattedText text={item.content} />}</p>
                <MessageActions structured={item.structured} interactive={item.id === latestAssistantId && !conversation?.readOnly} disabled={busy || Boolean(conversation?.readOnly)} onReply={(reply) => void send(reply)} />
              </div>
            </article>)}
            {optimisticMessage && <article className="agent-message agent-message-user agent-message-optimistic"><div className="agent-message-body"><p>{optimisticMessage}</p></div></article>}
            {canRevealAssistant && liveAssistantText && <article className="agent-message agent-message-assistant agent-message-live"><span className="agent-avatar" aria-hidden="true"><Bot size={18} strokeWidth={2} /></span><div className="agent-message-body"><p>{liveAssistantText}<span className="agent-stream-caret" aria-hidden="true" /></p></div></article>}
            {busy && !(canRevealAssistant && liveAssistantText) && <TypingIndicator label={busyLabel} visible={optimisticMessage ? showThinking : true} />}
            {canShowContext && <>
              {conversation?.status === "awaiting_clarification" && <CandidateGroup conversation={conversation} disabled={busy || conversation.readOnly} onSelect={(content, selection) => void send(content, [selection])} />}
              {conversation?.state.preview && <PreviewCard preview={conversation.state.preview} />}
              {conversation?.state.schedule?.preview && <SchedulePreviewCard preview={conversation.state.schedule.preview} />}
              {conversation?.state.customer?.preview && <CustomerPreviewCard preview={conversation.state.customer.preview} />}
              {conversation?.state.customer?.locationPreview && <LocationPreviewCard preview={conversation.state.customer.locationPreview} />}
              {conversation?.resultQuoteId && <div className="agent-result"><Check size={20} /><div><strong>Orçamento criado no sistema</strong><span>O registro já aparece no fluxo comercial.</span></div><Link className="site-button site-button-navy" to={`/orcamentos?quote=${conversation.resultQuoteId}`}>Abrir orçamento</Link></div>}
              {conversation?.pendingAction && !conversation.readOnly && <div className="agent-confirm-bar">
                <div className="agent-confirm-copy"><strong>{confirmationPrompt(conversation.pendingAction.type)}</strong><small>Nada foi salvo ainda. A ação só será criada depois da sua confirmação.</small></div>
                <div>
                  <button className="site-button" onClick={() => void cancel()} disabled={busy}><X size={17} />Descartar</button>
                  <button className="site-button site-button-red" onClick={() => void confirm()} disabled={busy}><Check size={17} />{confirmationLabel(conversation.pendingAction.type)}</button>
                </div>
              </div>}
            </>}
            <div ref={endRef} />
          </div>
          {error && <div className="form-error agent-error" role="alert">{error}</div>}
          <form className="agent-composer" onSubmit={submit}>
            <div className="agent-composer-inner">
              <div className="agent-composer-field"><textarea ref={composerRef} id={`agent-message-${presentation}`} rows={1} value={message} onChange={(event) => setMessage(event.target.value)} onKeyDown={handleComposerKeyDown} placeholder={composerPlaceholder} disabled={busy || conversation?.readOnly || (loading && !freshDraftMode)} aria-label="Mensagem" /></div>
              <button className="site-button site-button-red" disabled={busy || conversation?.readOnly || (loading && !freshDraftMode) || !message.trim()} aria-label="Enviar mensagem">{busy ? <LoaderCircle className="spin" size={18} /> : <Send size={18} />}</button>
            </div>
          </form>
      </section>
    </div>
  </div>;
}

function AgentMessageBubble({ content }: { content: string }) {
  return <article className="agent-message agent-message-assistant"><span className="agent-avatar" aria-hidden="true"><Bot size={18} strokeWidth={2} /></span><div className="agent-message-body"><p><FormattedText text={content} /></p></div></article>;
}

function FormattedText({ text }: { text: string }) {
  return <>{text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
      : part,
  )}</>;
}

function ProgressiveText({ text, onProgress, onDone }: { text: string; onProgress: () => void; onDone: () => void }) {
  const [visibleLength, setVisibleLength] = useState(0);
  const characters = Array.from(text);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisibleLength(characters.length);
      onDone();
      return;
    }
    setVisibleLength(0);
    let current = 0;
    const timer = window.setInterval(() => {
      current = Math.min(characters.length, current + Math.max(1, Math.ceil(characters.length / 90)));
      setVisibleLength(current);
      onProgress();
      if (current >= characters.length) {
        window.clearInterval(timer);
        onDone();
      }
    }, 18);
    return () => window.clearInterval(timer);
  }, [text]);

  return <>{characters.slice(0, visibleLength).join("")}<span className="agent-stream-caret" aria-hidden="true" /></>;
}

function TypingIndicator({ label, visible = true }: { label: string; visible?: boolean }) {
  return <article className={`agent-message agent-message-assistant agent-typing${visible ? " is-visible" : " is-reserved"}`} role={visible ? "status" : undefined} aria-hidden={!visible}><span className="agent-avatar" aria-hidden="true"><Bot size={18} strokeWidth={2} /></span><div className="agent-message-body"><p><span aria-hidden="true"><i /><i /><i /></span><small>{label}</small></p></div></article>;
}

function confirmationPrompt(type: "create_quote" | "create_appointment" | "create_customer" | "create_location" | "issue_fiscal" | "transition_work_order" | "report_field_cost" | "report_material" | "report_payment" | "report_occurrence" | "reschedule_appointment" | "cancel_appointment") {
  if (type === "create_appointment") return "Está tudo certo para criar este agendamento?";
  if (type === "create_customer") return "Está tudo certo para cadastrar este cliente?";
  if (type === "create_location") return "Está tudo certo para adicionar este endereço?";
  if (type === "issue_fiscal") return "Está tudo certo para solicitar esta emissão?";
  if (type === "transition_work_order") return "Está tudo certo para mudar o status desta OS?";
  if (type === "report_field_cost") return "Está tudo certo para registrar esta despesa de campo?";
  if (type === "report_material") return "Está tudo certo para registrar este material?";
  if (type === "report_payment") return "Está tudo certo para registrar este pagamento?";
  if (type === "report_occurrence") return "Está tudo certo para registrar esta ocorrência?";
  if (type === "reschedule_appointment") return "Está tudo certo para reagendar este serviço?";
  if (type === "cancel_appointment") return "Está tudo certo para cancelar este agendamento?";
  return "Está tudo certo para criar este orçamento?";
}

function confirmationLabel(type: "create_quote" | "create_appointment" | "create_customer" | "create_location" | "issue_fiscal" | "transition_work_order" | "report_field_cost" | "report_material" | "report_payment" | "report_occurrence" | "reschedule_appointment" | "cancel_appointment") {
  if (type === "create_appointment") return "Criar agendamento";
  if (type === "create_customer") return "Cadastrar cliente";
  if (type === "create_location") return "Adicionar endereço";
  if (type === "issue_fiscal") return "Solicitar emissão";
  if (type === "transition_work_order") return "Mudar status";
  if (type === "report_field_cost") return "Registrar despesa";
  if (type === "report_material") return "Registrar material";
  if (type === "report_payment") return "Registrar pagamento";
  if (type === "report_occurrence") return "Registrar ocorrência";
  if (type === "reschedule_appointment") return "Reagendar";
  if (type === "cancel_appointment") return "Cancelar agendamento";
  return "Criar orçamento";
}

function MessageActions({ structured, interactive, disabled, onReply }: {
  structured?: StructuredMessage;
  interactive: boolean;
  disabled: boolean;
  onReply: (reply: string) => void;
}) {
  const [openingDocument, setOpeningDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const documentResult = structured?.documentResult;

  const openDocument = async () => {
    if (!documentResult || openingDocument) return;
    setOpeningDocument(true);
    setDocumentError(null);
    const previewWindow = window.open("about:blank", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = `Abrindo ${documentResult.label}`;
      previewWindow.document.body.textContent = "Abrindo documento…";
    }
    try {
      const parsed = new URL(documentResult.fileUrl, window.location.origin);
      if (parsed.origin !== window.location.origin) throw new Error("O link deste documento não é válido.");
      const apiPath = `${parsed.pathname.startsWith("/v1/") ? parsed.pathname.slice(3) : parsed.pathname}${parsed.search}`;
      const blob = await apiBlob(apiPath);
      const blobUrl = URL.createObjectURL(blob);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.replace(blobUrl);
      } else {
        const fallbackLink = document.createElement("a");
        fallbackLink.href = blobUrl;
        fallbackLink.download = `${documentResult.label}.pdf`;
        fallbackLink.hidden = true;
        document.body.append(fallbackLink);
        fallbackLink.click();
        fallbackLink.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      previewWindow?.close();
      setDocumentError(error instanceof Error && error.message
        ? error.message
        : "Não foi possível abrir o documento. Tente novamente.");
    } finally {
      setOpeningDocument(false);
    }
  };

  if (!structured) return null;
  return <>
    {structured.financeSummary && <div className="agent-finance-summary">
      <span><small>Recebido</small><strong>{brl(structured.financeSummary.received)}</strong></span>
      <span><small>Custos</small><strong>{brl(structured.financeSummary.costs)}</strong></span>
      <span><small>Resultado</small><strong>{brl(structured.financeSummary.profit)}</strong></span>
    </div>}
    {interactive && structured.quickReplies?.length ? <div className="agent-quick-replies">{structured.quickReplies.slice(0, 2).map((reply) => <button key={reply} type="button" disabled={disabled} onClick={() => onReply(reply)}>{reply}</button>)}</div> : null}
    {documentResult && <>
      <button className="site-button site-button-red agent-action-link" type="button" disabled={disabled || openingDocument} onClick={() => void openDocument()} aria-busy={openingDocument}>
        {openingDocument ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
        {openingDocument ? "Abrindo documento…" : `Abrir ${documentResult.label}`}
      </button>
      {documentError && <p className="agent-document-error" role="alert">{documentError}</p>}
    </>}
    {structured.fiscalPreview && <div className="agent-fiscal-review"><span><small>Nota fiscal · {structured.fiscalPreview.workOrderNumber}</small><strong>{structured.fiscalPreview.customerName}</strong></span><strong>{brl(structured.fiscalPreview.total)}</strong><small>{structured.fiscalPreview.groups.length === 1 ? "1 enquadramento fiscal" : `${structured.fiscalPreview.groups.length} enquadramentos fiscais`}</small></div>}
    {structured.fiscalIssues?.length ? <div className="agent-fiscal-issue"><strong>{structured.fiscalIssues[0]?.message}</strong><span>{structured.fiscalIssues[0]?.action}</span></div> : null}
    {structured.fiscalStatus && <div className="agent-inline-result"><LoaderCircle size={18} /><span><strong>Nota fiscal em processamento</strong><small>Status: {structured.fiscalStatus.status}</small></span></div>}
    {structured.fiscalResult && <div className="agent-inline-result"><Check size={18} /><span><strong>{structured.fiscalResult.workOrderNumber}</strong><small>{structured.fiscalResult.documents.length === 1 ? "Nota enviada para emissão" : `${structured.fiscalResult.documents.length} notas enviadas para emissão`}</small></span></div>}
    {structured.scheduleResult && <div className="agent-inline-result"><CalendarCheck size={18} /><span><strong>{structured.scheduleResult.workOrderNumber}</strong><small>Agendamento confirmado</small></span></div>}
    {structured.customerResult && <div className="agent-inline-result"><Check size={18} /><span><strong>{structured.customerResult.legalName}</strong><small>Cliente cadastrado no sistema</small></span></div>}
    {structured.locationResult && <div className="agent-inline-result"><Check size={18} /><span><strong>{structured.locationResult.customerName}</strong><small>{structured.locationResult.address}</small></span></div>}
    {structured.actionLink && <Link className="site-button site-button-outline agent-action-link" to={structured.actionLink.to}>{structured.actionLink.label}<ChevronRight size={16} /></Link>}
  </>;
}

function CandidateGroup({ conversation, disabled, onSelect }: {
  conversation: Conversation; disabled: boolean;
  onSelect: (content: string, selection: Selection) => void;
}) {
  const groups: Array<{ title: string; candidates: Candidate[]; kind: Selection["kind"]; itemIndex?: number }> = [];
  if (conversation.state.customerCandidates?.length) groups.push({ title: "Encontrei estes clientes. Qual deles é?", candidates: conversation.state.customerCandidates, kind: "customer" });
  if (conversation.state.locationCandidates?.length) groups.push({ title: "Em qual endereço será o serviço?", candidates: conversation.state.locationCandidates, kind: "location" });
  conversation.state.items.forEach((item, itemIndex) => {
    if (item.candidates?.length) groups.push({ title: `Qual destes serviços é “${item.serviceQuery}”?`, candidates: item.candidates, kind: "service", itemIndex });
  });
  if (conversation.state.schedule?.responsibleCandidates?.length) groups.push({ title: "Quem será o responsável?", candidates: conversation.state.schedule.responsibleCandidates, kind: "employee" });
  const firstMissing = conversation.state.schedule?.missing[0] ?? conversation.state.missing[0] ?? "";
  const expectedKind: Selection["kind"] | undefined = firstMissing.includes("cliente") ? "customer"
    : firstMissing.includes("endereço") ? "location"
      : firstMissing.includes("serviço") ? "service"
        : firstMissing.includes("responsável") ? "employee"
          : undefined;
  const group = expectedKind ? groups.find(({ kind }) => kind === expectedKind) : groups[0];
  if (!group) return null;
  return <section className="agent-candidates"><strong>{group.title}</strong><div>{group.candidates.slice(0, 3).map((candidate) => <button key={candidate.id} disabled={disabled} onClick={() => onSelect(candidate.label, { kind: group.kind, id: candidate.id, ...(group.itemIndex === undefined ? {} : { itemIndex: group.itemIndex }) })}><span><strong>{candidate.label}</strong>{candidate.detail && <small>{candidate.detail}</small>}</span><ChevronRight size={17} /></button>)}</div>{group.candidates.length > 3 && <small>Se não for nenhum deles, escreva o nome para eu buscar de novo.</small>}</section>;
}

function PreviewCard({ preview }: { preview: Preview }) {
  return <article className="agent-preview"><header><div><span>Prévia do orçamento</span><strong>{preview.customerName}</strong></div><strong>{brl(preview.total)}</strong></header><p>{preview.locationLabel}</p><div className="agent-preview-items">{preview.items.map((item, index) => <div key={`${item.serviceName}-${index}`}><span><strong>{item.serviceName}</strong><small>{item.quantity} {item.unit} × {brl(item.unitPrice)}</small></span><strong>{brl(item.total)}</strong></div>)}</div><footer><span>Validade: {preview.validUntil.split("-").reverse().join("/")}</span><span>Pagamento: {paymentLabels[preview.paymentMethod] ?? preview.paymentMethod}</span></footer></article>;
}

function SchedulePreviewCard({ preview }: { preview: SchedulePreview }) {
  const formatDate = (value: string) => new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo",
  }).format(new Date(value));
  const visits = preview.visits?.length ? preview.visits : [{ startsAt: preview.startsAt, endsAt: preview.endsAt }];
  return <article className="agent-preview agent-schedule-preview"><header><div><span>{visits.length > 1 ? `Prévia · ${visits.length} dias na mesma OS` : "Prévia do agendamento"}</span><strong>{preview.customerName}</strong></div><strong>{brl(preview.agreedValue)}</strong></header><p>{preview.locationLabel}</p><div className="agent-preview-items"><div><span><strong>{preview.serviceName}</strong><small>{preview.sourceLabel}</small></span></div>{visits.map((visit, index) => <div key={`${visit.startsAt}:${index}`}><span><strong>{visits.length > 1 ? `Dia ${index + 1} · ` : ""}{formatDate(visit.startsAt)} — {formatDate(visit.endsAt)}</strong><small>Responsável: {preview.responsibleName}</small></span></div>)}</div></article>;
}

function CustomerPreviewCard({ preview }: { preview: CustomerPreview }) {
  const location = preview.location
    ? [
        [preview.location.street, preview.location.number].filter(Boolean).join(", "),
        preview.location.complement,
        preview.location.district,
        [preview.location.city, preview.location.state].filter(Boolean).join("/"),
      ].filter(Boolean).join(" · ")
    : "";
  const details = [
    preview.tradeName && ["Nome fantasia", preview.tradeName],
    preview.taxId && [preview.kind === "individual" ? "CPF" : "CNPJ", preview.taxId],
    preview.whatsapp && ["WhatsApp", preview.whatsapp],
    preview.phone && ["Telefone", preview.phone],
    preview.email && ["E-mail", preview.email],
  ].filter(Boolean) as string[][];
  return <article className="agent-preview agent-customer-preview">
    <header><div><span>Prévia do cliente</span><strong>{preview.legalName}</strong></div></header>
    {location && <p>{location}</p>}
    <div className="agent-preview-items">
      {details.length > 0
        ? details.map(([label, value]) => <div key={label}><span><small>{label}</small><strong>{value}</strong></span></div>)
        : <div><span><small>Cadastro inicial</small><strong>Somente nome/razão social</strong></span></div>}
    </div>
    <footer><span>{preview.kind === "individual" ? "Pessoa física" : "Pessoa jurídica"}</span>{preview.customerType && <span>{preview.customerType}</span>}</footer>
  </article>;
}

function LocationPreviewCard({ preview }: { preview: LocationPreview }) {
  const address = [
    `${preview.street}, ${preview.number ?? "s/n"}`,
    preview.complement,
    preview.district,
    `${preview.city}/${preview.state}`,
  ].filter(Boolean).join(" · ");
  return <article className="agent-preview agent-location-preview">
    <header><div><span>Prévia do endereço</span><strong>{preview.customerName}</strong></div></header>
    <p>{address}</p>
    <footer>{preview.postalCode && <span>CEP: {preview.postalCode}</span>}{preview.reference && <span>Referência: {preview.reference}</span>}</footer>
  </article>;
}
